/**
 * @file  src\pages\Home.js
 * @brief  This file is responsible for home page.
 * @date   June, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState, useEffect, useRef, useCallback, createContext } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import LeftpanelTab from "../../components/Onboarding/Dashboard/LeftpanelTab";
import SiteFooter from "../../components/Onboarding/SiteFooter";
import PropertyListing from "../../components/Onboarding/Dashboard/v2/PropertyListing";
import Offcanvas from "react-bootstrap/Offcanvas";
import SelectDropDown from "../../components/Common/SelectDropDown";
import MatchTutorial from "../../components/Tutorial/MatchTutorialModal";
import {
  showAgentPreferenceModal,
} from "../../reducers/ui";
import {
  BUY,
  WANT_AD_KEY,
  WANT_AD_PAGE_LIMIT,
  USER_INFO_KEY,
  HOME_PAGE,
  INVENTORY_KEY,
  STRENGTHEN_INVENTORY_KEY,
  USER_LOCATION_KEY,
  CLIENT_DETAILS_KEY,
  LONG_RENT,
  USER_TYPE_AGENT,
  USER_TYPE_MEMBER,
  DEFAULT_MAX_BUDGET_BUY,
  DEFAULT_MAX_BUDGET_LTR,
  DEFAULT_MAX_BUDGET_STR,
} from "../../constants/common";
import { getWantAds, getWantAdsPublic } from "../../services/wantAdService";
import { getMatches } from "@/services";
import HomeLoadingSkeleton from "../HomeLoadingSkeleton";
import {
  getLocalStorage,
  setLocalStorage,
  loggedInUser,
  removeMultipleLocalStorageItems,
  checkTutorialDisplayStatus,
  getUserType,
  getTitleText,
} from "../../helpers/common";
import {
  favouriteWantAd,
  unFavouriteWantAd,
} from "../../services/wantAdService";
import { toast } from "react-toastify";
import {
  getAgentsList,
  getUserInventoryCount,
} from "../../services/wantAdService";
import { handleTextBoxChange } from "../../actions/common";
import { pageURLs } from "@/constants";
import { getUserId } from "@/helpers";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import GeoLocation from "../../components/Common/GeoLocation";
import { usePrevious } from "../../hooks/usePrevious";
import { saveTutorialStatus } from "../../services/commonServices";
import { checkGitchaAvailable } from "../../services/commonServices";
import GitchaAvailableLocations from "../../components/GitchaAvailableLocationsScreen";
import RequestStayAvailability from "../../components/Common/RequestStayAvailability";
import SearchLocationScreen from "../../components/SearchLocationScreen";
import HeroSplash from "../../components/HeroSplash/HeroSplash";
import SearchTransition from "../../components/HeroSplash/SearchTransition";
import PreLaunchCityRequestModal from "../../components/Common/PreLauchCityRequestModal";
import WelcomeModal from "../../components/Onboarding/WelcomeModal";
import AgentLiketoStartModal from "@/components/Onboarding/UserRegister/AgentLiketoStartModal";
import { PageWrapper } from "@/components/PageWrapper";
import { useCurrentWebsite } from "@/hooks/useCurrentWebsite";
import GitchaNotAvailableModal from "@/components/Common/GitchaNotAvailableModal";
import FilterIcon from "../../images/filter-icon.svg";
import { useDispatch } from "react-redux";
import MatchHomeTabSkeleton from "@/components/Onboarding/Dashboard/MatchSkeleton";
import TenantLoginPage from "@/components/Common/TenantLoginPage/TenantLoginPage";
import FeedEmptyState from "@/components/AgentDashboard/FeedEmptyState/FeedEmptyState";
import { isMlsRestrictedTenant } from "@/helpers/tenant";
import { useAppConfig } from "@/appConfig";

export const filterContext = createContext();

const Home = () => {

  const [searchParams] = useSearchParams();
  const location = useLocation();
  const AdTypeFromUrl = searchParams.get("AdType");
  const showAgentModal = searchParams.get("showAgentModal");
  const tutorialPage = 1;
  const myRef = useRef(null);
  const navigate = useNavigate();

  const [ssoError] = useState(() => {
    const state = location.state;
    if (state?.ssoError) {
      window.history.replaceState({}, "");
      return state.ssoError;
    }
    return null;
  });

  const [mlsLinkState, setMlsLinkState] = useState(() => {
    const state = location.state;
    if (state?.pendingMlsLink && state?.pendingToken) {
      window.history.replaceState({}, "");
      return {
        show: true,
        pendingToken: state.pendingToken,
        maskedEmail: state.maskedEmail || "",
        redirect: state.redirect || null,
      };
    }
    return { show: false, pendingToken: null, maskedEmail: "", redirect: null };
  });
  const browserLocation = getLocalStorage("userLocation");

  // Session filter persistence - persists across navigation, clears on app close
  let savedSession = null;
  try {
    const data = sessionStorage.getItem("homeFilterSession");
    if (data) savedSession = JSON.parse(data);
  } catch { }
  const isRestoredRef = useRef(!!savedSession);
  const hasRestoredLocation = useRef(!!savedSession?.userLocation);
  const didSeedTenantLocation = useRef(false);

  const [showTransition, setShowTransition] = useState(false);
  const [transitionCity, setTransitionCity] = useState("");
  const handleTransitionComplete = useCallback(() => setShowTransition(false), []);

  const dispatch = useDispatch();

  const { mls } = useCurrentWebsite()

  const options = [
    { value: "newest", label: "Newest" },
    { value: "oldest", label: "Oldest" },
    { value: "highPrice", label: "Highest price" },
    { value: "lowPrice", label: "Lowest price" },
  ];
  const [showMatchTutorial, setMatchTutorial] = useState(false);
  const [currPage, setCurrPage] = useState(1); // storing current page number
  const [adList, setAdList] = useState([]);
  const [adListGitchaGenie, setAdListgitchaGenie] = useState([]);
  const [adType, setAdType] = useState(
    AdTypeFromUrl
      ? AdTypeFromUrl
      : savedSession?.filterQuery?.adType || BUY,
  );
  const filterTabKey = getLocalStorage("FilterTabKey");
  const previousBudgetMin = getLocalStorage("priceRangeMatch");
  const [totalCount, setTotalCount] = useState(null);
  const [totalCountMatch, setTotalCountMatch] = useState(null);
  const user = loggedInUser();
  const userType = getUserType();
  const { tenant } = useAppConfig() || {};
  const mlsRestricted = isMlsRestrictedTenant(tenant);
  const [sessionData, setSessionData] = useState(
    getLocalStorage(USER_INFO_KEY),
  );

  const [results, setResults] = useState([]);
  //form States on  agent client search
  const [formData, setFormData] = useState("");
  const [reloadAgents, setReloadAgents] = useState(false);
  const [showFooter, setShowFooter] = useState(false);
  const [scrollDirection, setScrollDirection] = useState(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [showNoResult, setShowNoResult] = useState(false);
  const [isFeedLoading, setIsFeedLoading] = useState(true);
  const [offCanvaShow, setOffCanvaShow] = useState(false);
  const [userLocation, setUserLocation] = useState(savedSession?.userLocation || null);
  const [isGitchaGenieFilter, setIsGitchaGenieFilter] = useState(
    filterTabKey === "match" ? true : false,
  );

  const [selectedProperty, setSelectedProperty] = useState(null);
  const [reload, setReload] = useState(false);
  const [sortParam, setSortParam] = useState(savedSession?.sortParam || options[0]);
  const [gitchaNotAvailble, setGitchaNotAvailable] = useState(
    userLocation?.isFromAvailableCities ? true : false,
  );
  const wantAdCount = adList?.length;
  const wantAdCountGitchaGenie = adListGitchaGenie?.length;
  const locationString = getLocalStorage("LocationString");
  const [isCityInPreLaunch, setIsCityInPreLaunch] = useState(false);
  const [PreLaunchStateName, setPreLaunchStateName] = useState(null);
  const [showGitchaGeineLabel, setShowGitchaGeineLabel] = useState(false);
  const [isLoadingMatches, setIsLoadingMatches] = useState(false);

  const [showRequestStayPopuop, setShowRequestStayPopup] = useState(false);
  const [showGitchaNotAvailablePopup, setShowGitchaNotAvailablePopup] = useState(false);
  const handleStayRequestPopupClose = () => {
    setShowRequestStayPopup(false);
  };
  /**
   * To handle user's default location when location permission is enabled
   * @param {*} location
   */
  // BLS users land on the feed, not HeroSplash. Seed userLocation from the
  // tenant's defaultMarket so the feed has a city to query against the moment
  // they sign in. handleUserLocation short-circuits on BLS so the browser
  // geolocation callback can't null this out.
  useEffect(() => {
    if (didSeedTenantLocation.current) return;
    if (!mlsRestricted || !user) return;
    if (userLocation) return;
    const dm = tenant?.defaultMarket;
    if (!dm?.city || !dm?.state) return;
    didSeedTenantLocation.current = true;
    // A session persisted while logged out (TenantLoginPage renders at /home)
    // can set isRestoredRef with no usable location, blocking the seeded coords
    // from reaching filterQuery. Clear it so the seed drives the first fetch.
    isRestoredRef.current = false;
    setUserLocation({
      city: dm.city,
      state: dm.state,
      latitude: dm.latitude,
      longitude: dm.longitude,
      formatted_address: `${dm.city}, ${dm.state}`,
    });
  }, [mlsRestricted, user, userLocation, tenant?.defaultMarket]);

  const handleUserLocation = async (location) => {
    // Skip browser geolocation override when we have a restored session location
    if (hasRestoredLocation.current) {
      hasRestoredLocation.current = false;
      return;
    }
    // BLS tenants seed from tenant.defaultMarket; don't let the browser
    // geolocation callback null that out (denied permission fires null).
    if (mlsRestricted) return;
    setLocalStorage("userLocation", location);
    if (location) {

      const reqPayLoad = {
        county: location.county,
        state: location.state,
      };
      await checkGitchaAvailable(reqPayLoad).then((data) => {
        if (_.get(data, "result") == true) {
          location["isFromAvailableCities"] = true;
          setGitchaNotAvailable(false);
          if (!locationString || locationString == "") {
            setUserLocation(location);
          }
        } else {
          if (!locationString || locationString == "") {
            setUserLocation(null);
          }
          setGitchaNotAvailable(true);
        }
      });
    } else {
      setUserLocation(null);
    }

  }
  let showNotification = checkTutorialDisplayStatus(tutorialPage);
  const [showNotificationIcon, setNotificationIcon] = useState(false);
  const PRICE_MIN = 0;
  const PRICE_MAX = null;
  const SQFT_MIN = null;
  const SQFT_MAX = null;
  const initialQuery =
    adType == "STR"
      ? {
        adType: adType,
        propertyType: [],
        alwpets: null,
        adaAccess: null,
        pvtBath: null,
        pvtEntrance: null,
        typeOfPlace: [10, 11, 6],
        priceMin: PRICE_MIN,
        priceMax: PRICE_MAX,
        sortType: sortParam?.value,
        bedrooms: "",
        beds: "",
        baths: "",
        sqrftMin: SQFT_MIN,
        sqrftMax: SQFT_MAX,
        lat: _.get(userLocation, "latitude", ""),
        long: _.get(userLocation, "longitude", ""),
        city: _.get(userLocation, "city", ""),
        state: _.get(userLocation, "state", ""),
        search: "",
        page: currPage,
        limit: WANT_AD_PAGE_LIMIT,
        fromDate: null,
        toDate: null,
        agentOnly: false,
        mls,
      }
      : adType == "LTR"
        ? {
          adType: adType,
          propertyType: [],
          typeOfPlace: [10, 11, 6],
          priceMin: PRICE_MIN,
          priceMax: PRICE_MAX,
          sortType: sortParam?.value,
          bedrooms: "",
          baths: "",
          sqrftMin: SQFT_MIN,
          sqrftMax: SQFT_MAX,
          search: "",
          lat: _.get(userLocation, "latitude", ""),
          long: _.get(userLocation, "longitude", ""),
          city: _.get(userLocation, "city", ""),
          state: _.get(userLocation, "state", ""),
          page: currPage,
          limit: WANT_AD_PAGE_LIMIT,
          agentOnly: false,
          mls,
        }
        : {
          adType: adType,
          propertyType: [],
          priceMin: PRICE_MIN,
          priceMax: PRICE_MAX,
          sortType: sortParam?.value,
          bedrooms: "",
          baths: "",
          sqrftMin: SQFT_MIN,
          sqrftMax: SQFT_MAX,
          lat: _.get(userLocation, "latitude", ""),
          long: _.get(userLocation, "longitude", ""),
          city: _.get(userLocation, "city", ""),
          state: _.get(userLocation, "state", ""),
          search: "",
          page: currPage,
          limit: WANT_AD_PAGE_LIMIT,
          agentOnly: false,
          mls,
        };

  const initialQueryGitchaGenie = {
    inventoryId: selectedProperty?.Inv_Id,
    adType: adType,
    city: selectedProperty?.City,
    state: selectedProperty?.State,
    priceMin: PRICE_MIN,
    priceMax: PRICE_MAX,
    sortType: sortParam?.value,
    page: currPage,
    limit: WANT_AD_PAGE_LIMIT,
  };

  const [filterQuery, setFilterQuery] = useState(savedSession?.filterQuery || initialQuery);
  const [filterQueryGitchaGenie, setFilterQueryGitchaGenie] = useState(
    initialQueryGitchaGenie,
  );

  // Save filter state to sessionStorage (called from event handlers, not useEffect)
  const saveFilterSession = (fq, ul, sp) => {
    // Don't persist a session from the BLS login page (logged-out /home renders
    // TenantLoginPage) - it stores an empty location and poisons the restore
    // gate after sign-in.
    if (mlsRestricted && !user) return;
    try {
      sessionStorage.setItem("homeFilterSession", JSON.stringify({
        filterQuery: fq,
        userLocation: ul,
        sortParam: sp,
      }));
    } catch { }
  };

  let DEFAULT_MAX_BUDGET;
  if (adType === BUY) {
    DEFAULT_MAX_BUDGET = DEFAULT_MAX_BUDGET_BUY;
  } else if (adType === LONG_RENT) {
    DEFAULT_MAX_BUDGET = DEFAULT_MAX_BUDGET_LTR;
  } else {
    DEFAULT_MAX_BUDGET = DEFAULT_MAX_BUDGET_STR;
  }

  const priceMinGitchaGenieFromStorage = previousBudgetMin
    ? previousBudgetMin[0]
    : null;
  const priceMaxGitchaGenieFromStorage = previousBudgetMin
    ? previousBudgetMin[1] == DEFAULT_MAX_BUDGET
      ? ""
      : previousBudgetMin[1]
    : null;

  const previousUserLocation = usePrevious(userLocation);

  useEffect(() => {
    // BLS tenants skip the Want-Listing-vs-inventory prompt - they're
    // dropped on the feed directly per the skip-onboarding policy.
    if (showAgentModal && !mlsRestricted) dispatch(showAgentPreferenceModal());
  }, [showAgentModal, dispatch, mlsRestricted]);


  useEffect(() => {
    if (isGitchaGenieFilter === true) {
      fetchWantAdsMatchingGitchaGenie(filterQueryGitchaGenie);
    }
  }, [isGitchaGenieFilter]);

  useEffect(() => {
    if (isRestoredRef.current) return;
    if (
      !isGitchaGenieFilter &&
      !_.isEqual(previousUserLocation, userLocation)
    ) {
      updateQuery({
        lat: _.get(userLocation, "latitude", null),
        long: _.get(userLocation, "longitude", null),
        city: _.get(userLocation, "city", null),
        state: _.get(userLocation, "state", null),
      });
      setCurrPage(1);
      setAdList([]);
    }
  }, [userLocation]);

  useEffect(() => {
    // BLS tenants bypass the Gitcha "available cities" gate - the MLS
    // owns the market and the feed should fetch any seeded location.
    if (
      !isGitchaGenieFilter &&
      (userLocation?.isFromAvailableCities || mlsRestricted) &&
      user
    ) {
      fetchWantAds(filterQuery);
    }
  }, [filterQuery]);

  useEffect(() => {
    if (!user) {
      fetchWantAdsPublic(filterQuery);
    }
  }, [filterQuery]);

  useEffect(() => {
    if (isGitchaGenieFilter) {
      fetchWantAdsMatchingGitchaGenie(filterQueryGitchaGenie);
    }
  }, [
    isGitchaGenieFilter,
    filterQueryGitchaGenie.inventoryId,
    filterQueryGitchaGenie.priceMin,
    filterQueryGitchaGenie.sortType,
    filterQueryGitchaGenie.priceMax,
    filterQueryGitchaGenie.pricePoint,
    filterQueryGitchaGenie.page,
    filterQueryGitchaGenie.adType,
    reload == true,
  ]);

  useEffect(() => {
    if (isRestoredRef.current) return;
    updateQuery(initialQuery);
    setCurrPage(1);
    setAdList([]);
    let showNotification = checkTutorialDisplayStatus(tutorialPage);
  }, [sessionData?.userInfo?.userId]);

  useEffect(() => {
    setTimeout(() => {
      setSessionData(getLocalStorage(USER_INFO_KEY));
    }, 1500);
  }, [user?.token?.accessToken]);

  useEffect(() => {
    if (isRestoredRef.current) return;
    if (isGitchaGenieFilter) {
      updateQueryGitchaGenie({ page: currPage });
    } else {
      updateQuery({ page: currPage });
    }
  }, [currPage]);

  useEffect(() => {
    if (isRestoredRef.current) return;
    if (isGitchaGenieFilter) {
      updateQueryGitchaGenie({ sortType: sortParam?.value });
    } else {
      updateQuery({ sortType: sortParam?.value });
    }
    setCurrPage(1);
    setAdList([]);
    setAdListgitchaGenie([]);
  }, [sortParam]);

  useEffect(() => {
    if (isRestoredRef.current) return;
    updateQueryGitchaGenie({
      inventoryId: selectedProperty?.Inv_Id,
      adType: adType,
      priceMin: priceMinGitchaGenieFromStorage || PRICE_MIN,
      priceMax: priceMaxGitchaGenieFromStorage || PRICE_MAX,
      city: selectedProperty?.City,
      state: selectedProperty?.State,
      sortType: sortParam?.value,
      page: currPage,
      limit: WANT_AD_PAGE_LIMIT,
    });
    updateQuery(initialQuery);
    setCurrPage(1);
    setAdList([]);
    setAdListgitchaGenie([]);
  }, [adType]);

  const clearFilterQuery = () => {
    sessionStorage.removeItem("homeFilterSession");
    if (!browserLocation?.isFromAvailableCities) {
      setUserLocation(null);
      setAdList([]);
      initialQuery["lat"] = null;
      initialQuery["long"] = null;
      initialQuery["city"] = null;
      initialQuery["state"] = null;
    } else {
      setUserLocation(browserLocation);
    }
    setCurrPage(1);
    setAdType(BUY);
    setFilterQuery(initialQuery);
  };

  const clearFilterGitchaGenie = () => {
    setFilterQueryGitchaGenie(initialQueryGitchaGenie);
  };

  /**
   * function to call Ad list
   * @param {*} query
   */
  const restoreScrollPosition = () => {
    const savedScrollY = sessionStorage.getItem("homeScrollY");
    if (savedScrollY) {
      setTimeout(() => {
        window.scrollTo(0, parseInt(savedScrollY, 10));
        sessionStorage.removeItem("homeScrollY");
        window.history.scrollRestoration = "auto";
      }, 500);
    }
  };

  const fetchWantAds = async (query) => {
    if (_.get(query, "lat", "") && _.get(query, "long", "")) {
      if (query.page === 1) setIsFeedLoading(true);
      await getWantAds(query)
        .then((response) => {
          if (response.errorCode === 0) {
            const isPreLauch = _.get(response, "result.isPrelaunch", null);
            const StateName = _.get(response, "result.StateName", null);
            if (isPreLauch) {
              setIsCityInPreLaunch(true);
              setPreLaunchStateName(StateName);
            } else {
              const responseResult = _.get(response, "result.data", null);
              setTotalCount(_.get(response, "result.totalCount", "0"));
              if (responseResult.length > 0) {
                setShowNoResult(false);
                if (query.page === 1) {
                  setAdList(responseResult);
                  restoreScrollPosition();
                } else {
                  setAdList([...adList, ...responseResult]);
                }
              } else if (
                responseResult &&
                responseResult.length === 0 &&
                query.page === 1
              ) {
                setShowNoResult(true);
                setAdList([]);
              }
            }
          }
          // Clear loading for every resolved response - including a non-zero
          // errorCode - so the empty state can take over instead of the skeleton.
          if (query.page === 1) setIsFeedLoading(false);
        })
        .catch((err) => {
          console.log("Error", err);
          if (mlsRestricted && query.page === 1) {
            setShowNoResult(true);
            setAdList([]);
          }
          if (query.page === 1) setIsFeedLoading(false);
        });
    }
  };

  const fetchWantAdsPublic = async (query) => {
    if (_.get(query, "lat", "") && _.get(query, "long", "")) {
      if (query.page === 1) setIsFeedLoading(true);
      await getWantAdsPublic(query)
        .then((response) => {
          if (response.errorCode === 0) {
            const isPreLauch = _.get(response, "result.isPrelaunch", null);
            const StateName = _.get(response, "result.StateName", null);
            if (isPreLauch) {
              setIsCityInPreLaunch(true);
              setPreLaunchStateName(StateName);
            } else {
              const responseResult = _.get(response, "result.data", null);
              setTotalCount(_.get(response, "result.totalCount", "0"));
              if (responseResult.length > 0) {
                setShowNoResult(false);
                if (query.page === 1) {
                  setAdList(responseResult);
                  restoreScrollPosition();
                } else {
                  setAdList([...adList, ...responseResult]);
                }
              } else if (
                responseResult &&
                responseResult.length === 0 &&
                query.page === 1
              ) {
                setShowNoResult(true);
                setAdList([]);
              }
            }
          }
          if (query.page === 1) setIsFeedLoading(false);
        })
        .catch((err) => {
          console.log("Error", err);
          if (query.page === 1) setIsFeedLoading(false);
        });
    }
  };

  /**
 * To fetch match want ads
 * @param {*} query
 */
  const fetchWantAdsMatchingGitchaGenie = async (query) => {
    if (!query.inventoryId) {
      setShowNoResult(true);
      setTotalCountMatch(null);
      return;
    }

    setShowNoResult(false);
    setIsLoadingMatches(true);

    const mapMatchSort = (val) => {
      switch ((val || "").toLowerCase()) {
        case "newest":
          return "MOST_RECENT";
        case "oldest":
          return "OLDEST";
        // price sorts are not applicable in match mode, default to relevance
        case "highprice":
        case "lowprice":
        default:
          return "MOST_RELEVANT";
      }
    };

    try {
      const request = {
        inventoryId: query.inventoryId,
        page: query.page,
        limit: query.limit,
        sortType: mapMatchSort(query.sortType),
        minScore: 60,
        userId: getUserId(),
        ...(query.pricePoint != null && { pricePoint: query.pricePoint }),
      };

      const response = await getMatches(request);
      const payload = _.get(response, "data.data", []);
      const totalCount = _.get(response, "data.totalCount", 0);

      setShowGitchaGeineLabel(true);
      setTotalCountMatch(totalCount);

      if (Array.isArray(payload) && payload.length > 0) {
        if (query.page === 1) {
          setAdListgitchaGenie([...payload]);
          restoreScrollPosition();
        } else {
          setAdListgitchaGenie([...adListGitchaGenie, ...payload]);
        }
        setShowNoResult(false);
      } else if (query.page === 1) {
        setAdListgitchaGenie([]);
        setShowNoResult(true);
      }
    } catch (err) {
      toast.dismiss();
      toast.error(err?.message || "Failed to load matches");
      setAdListgitchaGenie([]);
      setShowNoResult(true);
      setTotalCountMatch(0);
    } finally {
      setIsLoadingMatches(false);
    }
  };


  const updateFilter = (stateObject) => {
    isRestoredRef.current = false;
    setCurrPage(1);
    updateQuery(stateObject);
  };

  const updateFilterGitchaGenie = (stateObject) => {
    if (isGitchaGenieFilter) {
      setCurrPage(1);
      updateQueryGitchaGenie(stateObject);
    }
  };

  const updateQuery = (stateObject) => {
    const newFilterQuery = { ...filterQuery, ...stateObject };
    setFilterQuery(newFilterQuery);
    saveFilterSession(newFilterQuery, userLocation, sortParam);
  };

  const updateQueryGitchaGenie = (stateObject) => {
    Object.keys(stateObject).forEach(function (key) {
      setFilterQueryGitchaGenie((prevState) => ({
        ...prevState,
        [key]: stateObject[key],
      }));
    });
  };

  const prevScrollPosition = usePrevious(scrollPosition);
  /**
   * To handle scroll
   */
  const handleScroll = () => {
    const el = myRef.current;
    const scrollHeight = el.scrollHeight;
    const scrollTop = el.scrollTop;
    const clientHeight = el.clientHeight;

    // if (
    //   scrollHeight !== clientHeight &&
    //   scrollTop !== 0 &&
    //   Math.abs(scrollHeight - (scrollTop + clientHeight)) <= 1
    // ) {
    //   setShowFooter(true);
    //   setScrollDirection("down");
    // } else if (scrollTop < prevScrollPosition && scrollDirection !== "up") {
    //   setShowFooter(false);
    //   setScrollDirection("up");
    // }
    // setScrollPosition(scrollTop);
  };

  const handleShowMoreClick = () => {
    isRestoredRef.current = false;
    setShowFooter(false);
    setCurrPage((prevState) => prevState + 1);
  };

  /**
   * To handle the want-ad favorite/unfavorite
   */

  const handleFavoriteOrUnfavoriteWantAd = async (
    IsFavourite,
    Want_ad_Id,
    clientId = [],
  ) => {
    if (Want_ad_Id) {
      let data = {
        clientId: IsFavourite == false ? clientId : null,
        wantAdId: Want_ad_Id,
      };
      await (
        IsFavourite == false ? favouriteWantAd(data) : unFavouriteWantAd(data)
      )
        .then((response) => {
          if (response.errorCode == 0) {
            let message = "";
            if (clientId.length > 0) {
              message =
                "Want-Listing has been favorited for " +
                clientId.length +
                (clientId.length > 1 ? " clients" : " client");
            } else {
              message =
                IsFavourite == true
                  ? "Want-Listing has been removed from your favorites"
                  : "Want-Listing has been added to your favorites";
            }

            const favToastId = "feed-favorite-toast";
            toast.dismiss(favToastId);
            toast.success(message, { toastId: favToastId, autoClose: 1000 });

            const newFavValue = IsFavourite == false ? 1 : 0;
            const applyFav = (items) =>
              (items || []).map((item) =>
                item.Want_ad_Id == Want_ad_Id
                  ? { ...item, IsFavourite: newFavValue }
                  : item,
              );
            setAdList((prev) => applyFav(prev));
            setAdListgitchaGenie((prev) => applyFav(prev));
          } else {
            console.log("Something went wrong");
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  };

  //Remove existing all localStorage keys except userInfo.
  useEffect(() => {
    removeMultipleLocalStorageItems([
      WANT_AD_KEY,
      INVENTORY_KEY,
      STRENGTHEN_INVENTORY_KEY,
      USER_LOCATION_KEY,
      CLIENT_DETAILS_KEY,
    ]);
  }, []);

  /**
   *
   */
  useEffect(() => {
    getAgentsClientsList();
  }, [formData, reloadAgents, sessionData?.userInfo?.userId]);

  /**
   * To handle textbox value change on agent client search
   * @param {any} event
   */
  const onChangeText = (event) => {
    let value = "";
    if (event) {
      value = handleTextBoxChange(event);
    }

    setFormData((formData) => value);
  };

  /**
   * To get agents clients list
   * @param {*} query
   */
  const getAgentsClientsList = async (query) => {
    let a = {
      search: formData,
      sortBy: "createdOn",
      sortDir: "desc",
    };
    await getAgentsList(a)
      .then((response) => {
        const responseResult = _.get(response, "result", null);
        if (response.errorCode === 0 && responseResult) {
          setResults(responseResult);
        } else {
          console.log("Something went wrong");
        }
      })
      .catch((err) => {
        console.log("Error", err);
      });
  };

  const handleWantAdItemClick = (wantAdId, slug) => {
    sessionStorage.setItem("homeScrollY", window.scrollY);
    window.history.scrollRestoration = "manual";
    const sessionUserInfo = getLocalStorage(USER_INFO_KEY);
    if (sessionUserInfo?.userInfo?.userId) {
      setLocalStorage(WANT_AD_KEY, {
        wantAdId: wantAdId,
        isFromHomeFeed: true,
      });
      navigate(`${pageURLs.wantAdDetails}/${slug}`);
    } else {
      navigate(`${pageURLs.wantAdDetails}/${slug}`);
    }
  };

  const handleGitchGenieFilter = (value) => {
    setIsGitchaGenieFilter(value);
  };

  const handleSortParam = (selectedParam) => {
    isRestoredRef.current = false;
    setSortParam(selectedParam);
  };
  const getLocationText = () => {
    if (isGitchaGenieFilter) {
      return totalCountMatch != null && totalCountMatch > 0
        ? `${totalCountMatch} Matches to Want-Listings`
        : "";
    } else {
      return totalCount != null
        ? `Real Time Home ${adType == BUY
          ? " Buyers"
          : "Renters"
        }   In/Near ${_.get(userLocation, "formatted_address", "")}`
        : "";
    }
  };

  const ref = useRef(null);

  const handleOnMouseEnter = () => {
    setMatchTutorial(true);
  };
  const handleToggle = async () => {
    let tutorialDisplayedPages = getLocalStorage("tutorialDisplayedPages");
    tutorialDisplayedPages = tutorialDisplayedPages
      ? tutorialDisplayedPages
      : [];
    tutorialDisplayedPages.push(1); //
    setLocalStorage("tutorialDisplayedPages", tutorialDisplayedPages);
    setNotificationIcon(false);
    if (sessionData && sessionData?.userInfo?.userId) {
      await saveTutorialStatus(1, 1); //Save status
    }
  };
  const [showWelcomeModal, setShowWelcomeModal] = useState();

  const handleCloseModal = () => {
    setShowWelcomeModal(false);
  };

  useEffect(() => {
    if (
      showNotification === true &&
      sessionData &&
      sessionData?.userInfo?.userId
    ) {
      checkUserInventoryCount();
    }
  }, []);

  const checkUserInventoryCount = async () => {
    let inventoryCount = await getUserInventoryCount();
    const responseResult = _.get(inventoryCount, "result", null);
    setNotificationIcon(false);
    if (inventoryCount.errorCode == 0 && responseResult) {
      if (responseResult[0].count > 0) {
        setNotificationIcon(true);
      }
    }
  };
  /**
   * To check gitcha is available in searched location
   * @param {*} city
   * @param {*} state
   * @param {*} county
   * @param {*} formatted_address
   * @param {*} locationObject
   */
  const checkGitchaAvailableInLoc = async (
    city,
    state,
    county,
    formatted_address,
    locationObject,
  ) => {
    isRestoredRef.current = false;
    try {
      const reqPayLoad = {
        county: county,
        state: state,
        city: city,
      };
      const response = await checkGitchaAvailable(reqPayLoad);
      if (response.result) {
        setUserLocation({ ...locationObject, isFromAvailableCities: true });
        setGitchaNotAvailable(false);
      } else {
        setUserLocation({ ...locationObject, isFromAvailableCities: false });
        setGitchaNotAvailable(true);
        setShowGitchaNotAvailablePopup(true);
      }
    } catch (error) {
      setGitchaNotAvailable(true);
      setShowGitchaNotAvailablePopup(true);
    }
  };

  const selectedClient = getLocalStorage("MatchSelectedClient");
  const currentSelectedProperty = getLocalStorage("selectedPropertyId");
  const isPremiumUser = getLocalStorage("isPremiumUser");

  /**
   * To handle rendering result
   * @returns
   */
  const renderResult = () => {
    if (isGitchaGenieFilter) {
      if (
        showNoResult &&
        adListGitchaGenie &&
        adListGitchaGenie.length === 0 &&
        currentSelectedProperty?.Inv_Id == null
      ) {
        return <MatchHomeTabSkeleton />;
      } else if (
        showNoResult &&
        adListGitchaGenie &&
        adListGitchaGenie.length === 0 &&
        currentSelectedProperty?.Inv_Id
      ) {
        if (
          (userType == USER_TYPE_AGENT && isPremiumUser == 1) ||
          (userType == USER_TYPE_AGENT && selectedClient) ||
          userType == USER_TYPE_MEMBER
        ) {
          return (
            <div className="no-result-wrap">
              <Button
                className="filter-button filter-icon-btn"
                onClick={() => setOffCanvaShow(true)}
              >
                <img src={FilterIcon} alt="Filters" />
              </Button>
              <>
                <h3>
                  It looks like your property doesn’t have any matches yet, but
                  we’ll notify you as soon as you have a match!
                </h3>
                <p>
                  In the meantime, you can add another property or post a
                  want-listing.
                </p>
              </>
            </div>
          );
        }
      } else if (
        !showNoResult &&
        adListGitchaGenie &&
        adListGitchaGenie.length === 0
      ) {
        return (
          <>
            <div
              className={
                !showFooter
                  ? "property-listing-outer "
                  : "property-listing-outer"
              }
              onScroll={handleScroll}
              ref={myRef}
            >
              <HomeLoadingSkeleton />
            </div>
          </>
        );
      } else {
        return (
          <div
            className={
              !showFooter ? "property-listing-outer " : "property-listing-outer"
            }
            onScroll={handleScroll}
            ref={myRef}
          >
            <div style={{ opacity: isLoadingMatches ? 0.5 : 1, transition: "opacity 0.2s ease", pointerEvents: isLoadingMatches ? "none" : "auto" }}>
              {adListGitchaGenie &&
                adListGitchaGenie.map((wa, index) => (
                  <PropertyListing
                    list={wa}
                    key={index}
                    handleFavoriteOrUnfavoriteWantAd={
                      handleFavoriteOrUnfavoriteWantAd
                    }
                    results={results}
                    clientSearchText={formData}
                    onChangeText={onChangeText}
                    onAddClient={() => setReloadAgents(!reloadAgents)}
                    handleWantAdItemClick={handleWantAdItemClick}
                  />
                ))}
              {showGitchaGeineLabel &&
                wantAdCountGitchaGenie < totalCountMatch && (
                  <div className="text-center pt-3 pb-3 w-100">
                    <button
                      className="btn btn-primary"
                      onClick={() => handleShowMoreClick()}
                    >
                      Show More
                    </button>
                  </div>
                )}
            </div>
          </div>
        );
      }
    } else {
      if (!userLocation?.isFromAvailableCities && !mlsRestricted) {
        return (
          <>
            <div className="no-result-wrap">
              <GitchaAvailableLocations
                setUserLocation={setUserLocation}
                userLocation={userLocation}
                checkGitchaAvailableInLoc={checkGitchaAvailableInLoc}
              />
            </div>
          </>
        );
      } else if (
        mlsRestricted &&
        (searchParams.get("showEmptyState") === "1" ||
          (!isFeedLoading && adList && adList.length === 0))
      ) {
        return (
          <div className="property-listing-outer">
            <FeedEmptyState
              onCta={() =>
                navigate(pageURLs.agentCreatewantadMainScreen)
              }
            />
          </div>
        );
      } else if (showNoResult && adList && adList.length === 0) {
        return (
          <div className="no-result-wrap">
            <Button
              className="filter-button filter-icon-btn"
              onClick={() => setOffCanvaShow(true)}
            >
              <img src={FilterIcon} alt="Filters" />
            </Button>
            <>
              <h3>There aren't any want-listings in your location yet</h3>
              <p>In the mean time, try broadening your location search</p>
            </>
          </div>
        );
      } else if (!showNoResult && adList && adList.length === 0) {
        return (
          <>
            <div
              className={
                !showFooter
                  ? "property-listing-outer "
                  : "property-listing-outer"
              }
              onScroll={handleScroll}
              ref={myRef}
            >
              <HomeLoadingSkeleton />
            </div>
          </>
        );
      } else {
        return (
          <div
            className={
              !showFooter ? "property-listing-outer " : "property-listing-outer"
            }
            onScroll={handleScroll}
            ref={myRef}
          >
            {adList &&
              adList.map((wa, index) => (
                <PropertyListing
                  list={wa}
                  key={index}
                  handleFavoriteOrUnfavoriteWantAd={
                    handleFavoriteOrUnfavoriteWantAd
                  }
                  results={results}
                  clientSearchText={formData}
                  onChangeText={onChangeText}
                  onAddClient={() => setReloadAgents(!reloadAgents)}
                  handleWantAdItemClick={handleWantAdItemClick}
                />
              ))}
            {wantAdCount < totalCount && (
              <div className="text-center pt-3 pb-3 w-100">
                <button
                  className="btn btn-primary"
                  onClick={() => handleShowMoreClick()}
                >
                  Show More
                </button>
              </div>
            )}
          </div>
        );
      }
    }
  };

  const hasFeedListings = isGitchaGenieFilter
    ? adListGitchaGenie && adListGitchaGenie.length > 0
    : adList && adList.length > 0;

  if (mlsRestricted && !user) {
    return (
      <TenantLoginPage
        ssoError={ssoError}
        mlsLinkState={mlsLinkState}
        onVerifySuccess={() => {
          const dest = mlsLinkState.redirect && mlsLinkState.redirect.startsWith("/") && !mlsLinkState.redirect.startsWith("//")
            ? mlsLinkState.redirect
            : pageURLs.home;
          setMlsLinkState({ show: false, pendingToken: null, maskedEmail: "", redirect: null });
          navigate(dest, { replace: true });
          window.location.reload();
        }}
        onVerifyClose={() => {
          setMlsLinkState({ show: false, pendingToken: null, maskedEmail: "", redirect: null });
          navigate(pageURLs.home, { replace: true });
        }}
      />
    );
  }

  return (
    <>
      {sessionData ? (
        <PreLaunchCityRequestModal
          show={isCityInPreLaunch}
          onHide={() => {
            setIsCityInPreLaunch(false);
            setPreLaunchStateName(null);
          }}
          setUserLocation={setUserLocation}
          userLocation={userLocation}
          PreLaunchStateName={PreLaunchStateName}
        />
      ) : (
        <></>
      )}
      {showWelcomeModal && (
        <WelcomeModal
          name={getLocalStorage("userInfo")?.userInfo?.firstName}
          videoUrl={
            "https://gitcha-uploads.s3.us-east-1.amazonaws.com/public/First+Steps_+Quick+5+Min+Walkthrough+of+Gitcha.mp4"
          }
          showModal={showWelcomeModal}
          onClose={handleCloseModal}
        />
      )}
      <MatchTutorial
        show={showMatchTutorial}
        onHide={() => {
          handleToggle();
          setMatchTutorial(false);
        }}
      />
      <RequestStayAvailability
        show={showRequestStayPopuop}
        locationFromSTorage={browserLocation}
        onHide={handleStayRequestPopupClose}
      />
      <GeoLocation handleUserLocation={handleUserLocation} page={HOME_PAGE} />
      <filterContext.Provider
        value={{
          // handleAdTypeClick,
          adType: BUY,
          clearFilterQuery,
          adListGitchaGenie,
          totalCountMatch,
          updateQueryGitchaGenie,
        }}
      >

        <PageWrapper
          headerSidebarProps={{
            onLoginSuccess: () => {
              setCurrPage(1);
              setAdList([]);
              const updatedUser = loggedInUser();
              if (updatedUser && userLocation?.isFromAvailableCities) {
                fetchWantAds({ ...filterQuery, page: 1 });
              }
            }
          }}
        >
          {showTransition && (
            <SearchTransition
              show={showTransition}
              cityName={transitionCity}
              onComplete={handleTransitionComplete}
            />
          )}
          {userLocation === null && !isGitchaGenieFilter && !mlsRestricted ? (
            <HeroSplash
              setUserLocation={setUserLocation}
              adType={adType}
              onSearchStart={(locationObj, city, state, county, formatted_address) => {
                setTransitionCity(locationObj.formatted_address);
                setShowTransition(true);
                setTimeout(() => {
                  checkGitchaAvailableInLoc(city, state, county, formatted_address, locationObj);
                }, 100);
              }}
            />
          ) : (
            <div className="d-flex flex-row">
              <Offcanvas
                show={offCanvaShow}
                onHide={() => setOffCanvaShow(false)}
                placement="start"
                className="pushmenu"
              >
                <Offcanvas.Header closeButton>
                  <h2>Filter</h2>
                </Offcanvas.Header>
                <Offcanvas.Body>
                  <div className="left-panel">
                    <LeftpanelTab
                      updateFilter={updateFilter}
                      filterQuery={filterQuery}
                      handleGitchGenieFilter={handleGitchGenieFilter}
                      clearFilterGitchaGenie={clearFilterGitchaGenie}
                      updateFilterGitchaGenie={updateFilterGitchaGenie}
                      selectedProperty={selectedProperty}
                      setSelectedProperty={setSelectedProperty}
                      setUserLocation={setUserLocation}
                      userLocation={userLocation}
                      gitchaNotAvailble={gitchaNotAvailble}
                      checkGitchaAvailableInLoc={checkGitchaAvailableInLoc}
                    />
                  </div>
                </Offcanvas.Body>
              </Offcanvas>
              <div className="pe-md-3 pe-lg-3 pe-xl-4 d-none d-md-block home-left-pannel-wrapper">
                <div className="left-panel position-relative">
                  {showNotificationIcon && (
                    <div className="ring-container">
                      <div className="circlebtn">
                        <div className="ringring"></div>
                      </div>
                    </div>
                  )}

                  <div ref={ref} className="w-100 position-relative">
                    {showNotificationIcon && (
                      <button
                        className="btn red-hotspot-btn position-absolute"
                        onClick={() => setMatchTutorial(true)}
                        onMouseEnter={handleOnMouseEnter}
                      ></button>
                    )}
                  </div>

                  <LeftpanelTab
                    updateFilter={updateFilter}
                    filterQuery={filterQuery}
                    handleGitchGenieFilter={handleGitchGenieFilter}
                    clearFilterGitchaGenie={clearFilterGitchaGenie}
                    updateFilterGitchaGenie={updateFilterGitchaGenie}
                    selectedProperty={selectedProperty}
                    setSelectedProperty={setSelectedProperty}
                    setUserLocation={setUserLocation}
                    userLocation={userLocation}
                    gitchaNotAvailble={gitchaNotAvailble}
                    checkGitchaAvailableInLoc={checkGitchaAvailableInLoc}
                  />
                </div>
              </div>
              <div className="ps-md-3 ps-lg-3 ps-xl-4 home-listing-wrapper">
                {userLocation === null && !isGitchaGenieFilter ? (
                  <SearchLocationScreen
                    setUserLocation={setUserLocation}
                    handleGitchaNotAvailableLocation={() => { }}
                    gitchaNotAvailble={gitchaNotAvailble}
                    checkGitchaAvailableInLoc={checkGitchaAvailableInLoc}
                    adType={adType}
                    showFilters={() => setOffCanvaShow(true)}
                  />
                ) : (
                  <div className="right-panel">
                    {hasFeedListings &&
                      <div className="top-sec mb-24 md-mt-24">
                        <div className="d-flex align-items-center results-header-row">
                          <div className="col results-title-col">
                            {getTitleText(
                              userLocation,
                              isGitchaGenieFilter,
                              isCityInPreLaunch,
                              getLocationText,
                            )}
                          </div>
                          <div className="d-flex align-items-center results-controls">
                            <div className="d-flex select-wrap position-relative gap-3 align-items-center">
                              <Form.Label style={{ fontSize: 15 }} className="mb-0">
                                Sort By:
                              </Form.Label>
                              <SelectDropDown
                                id="multi3"
                                name="colors3"
                                options={options}
                                className="basic-single sort-select d-inline-block mb-0"
                                classNamePrefix="select"
                                placeholder="Select"
                                value={sortParam}
                                onChange={handleSortParam}
                                styles={{
                                  dropdownIndicator: (provided, state) => ({
                                    ...provided,
                                    transform:
                                      state.selectProps.menuIsOpen &&
                                      "rotate(180deg)",
                                  }),
                                }}
                                errorClass="d-none"
                              />
                            </div>
                            <Button
                              className="filter-icon-btn results-filter-btn"
                              onClick={() => setOffCanvaShow(true)}
                            >
                              <img src={FilterIcon} alt="Filters" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    }
                    {renderResult()}
                  </div>
                )}
              </div>
            </div>
          )}
          {!mlsRestricted && <SiteFooter show={showFooter} />}
        </PageWrapper>

      </filterContext.Provider>

      <AgentLiketoStartModal />
      <GitchaNotAvailableModal
        show={showGitchaNotAvailablePopup}
        onHide={() => setShowGitchaNotAvailablePopup(false)}
      />
    </>
  );
};

export default Home;